// renderAggregatedDamageEvent.tsx

import React, { useContext } from 'react';
import { BaseRendererProps } from './eventRenderers';
import { getCritDamageTypeIcon, getDamageTypeIcon, DamageSubType } from './utils';
import GameAssetsContext from '../../../../../../context/GameAssetContext';
import { faTurnUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './renderAggregatedDamageEvent.css';
import { getUniversalSourceIcon } from './StatChangeEvent';

const RenderAggregatedDamageEvent: React.FC<BaseRendererProps> = ({ log }) => {
  const { cumulativeDamage, tickCount } = log.details;

  const { gameWideAssets, championsAssets } = useContext(GameAssetsContext);
  const { critIcons } = gameWideAssets;

  const details = log.details;
  const isCrit = details.isCrit || false;
  const damageSubType = details.damageSubType;
  const sourceType = details.sourceType || 'Ability';
  const sourceName = details.source;
  const imageState = log.imageState;
  const abilityKey = details.spellKey;
  const attackerName = details.attackerName;
  const petName = details.petName; // For pet damage sources

  // Calculate individual damage and display values
  const totalDamage = Math.floor(cumulativeDamage);
  const individualDamage = Math.round(cumulativeDamage / tickCount);
  const mainAmountDisplay = totalDamage.toString();
  const bracketedDisplay = `(${tickCount} × ${individualDamage})`;

  // Create tooltip text with rounding note
  const damageTypeText = damageSubType === DamageSubType.Physical ? 'Physical' : 
                        damageSubType === DamageSubType.Magic ? 'Magic' : 'True';
  const tooltipText = `Total ${damageTypeText} Damage: ${totalDamage}, with ${tickCount} instances of approximately ${individualDamage} damage.\n\nNote: Individual damage values may vary due to rounding calculations.`;

  // Determine the damage icon based on whether it's a crit
  let damageIcon = null;
  if (isCrit) {
    damageIcon = getCritDamageTypeIcon(damageSubType, critIcons);  
  } else {
    damageIcon = getDamageTypeIcon(damageSubType);
  }

  // Use the universal image fetcher for source icon (supports pets, items, runes, abilities, etc.)
  const sourceIcon = getUniversalSourceIcon(
    {
      sourceType: sourceType,
      imageState: imageState,
      sourceName: sourceName,
      championName: attackerName,
      abilityKey: abilityKey || imageState,
      petName: petName, // Pass pet name for pet damage
    },
    gameWideAssets,
    championsAssets
  );
  

  return (
    <div className="aggregated-damage-event" key={log.id} title={tooltipText}>
      <div className="damage-amount-wrapper">
        {damageIcon}
        <div className="damage-amounts">
          <span className="damage-amount">{mainAmountDisplay}</span>
          <span className="damage-bracketed">{bracketedDisplay}</span>
        </div>
      </div>
      {sourceIcon && (
        <div className="damage-source-wrapper">
          <FontAwesomeIcon icon={faTurnUp} className="fa-turn-up" />
          {sourceIcon}
        </div>
      )}
    </div>
  );
};

export default RenderAggregatedDamageEvent;